/**
 * Canon Validator Module
 * 
 * Validates that fault IDs are in the canon taxonomy before rendering.
 * Drops non-canon faults with appropriate logging.
 */

import canonTaxonomyMap from '../../../canon-taxonomy-map.json';

/**
 * Canon taxonomy map structure
 */
interface CanonTaxonomyMap {
  map_version: string;
  canon_faults: Array<{
    fault_id: string;
    taxonomy_role: string;
    [key: string]: any;
  }>;
  [key: string]: any;
}

// Cache canon fault IDs for fast lookup
let cachedCanonFaultIds: Set<string> | null = null;

/**
 * Get set of canon fault IDs from canon-taxonomy-map.json
 * Cached for performance
 */
function getCanonFaultIds(): Set<string> {
  if (cachedCanonFaultIds !== null) {
    return cachedCanonFaultIds;
  }
  
  const map = canonTaxonomyMap as unknown as CanonTaxonomyMap;
  const faultIds = new Set<string>();
  
  for (const fault of map.canon_faults || []) {
    if (fault.taxonomy_role === 'canon_fault' && fault.fault_id) {
      faultIds.add(fault.fault_id.toLowerCase().trim());
    }
  }
  
  cachedCanonFaultIds = faultIds;
  return faultIds;
}

/**
 * Validate that a fault ID is a canon fault
 * 
 * @param faultId - The fault ID to validate
 * @returns True if the fault is in the canon taxonomy
 */
export function isCanonFault(faultId: string): boolean {
  const normalizedId = faultId.toLowerCase().trim();
  const canonFaultIds = getCanonFaultIds();
  return canonFaultIds.has(normalizedId);
}

/**
 * Validate canon fault and log if non-canon
 * 
 * @param faultId - The fault ID to validate
 * @param context - Optional context for logging (analysis_id, screen_id, etc.)
 * @returns True if canon fault, false if non-canon (and logs telemetry)
 */
export function validateCanonFault(
  faultId: string,
  context?: { analysis_id?: string; screen_id?: string; [key: string]: any }
): boolean {
  const normalizedId = faultId.toLowerCase().trim();
  const isValid = isCanonFault(normalizedId);
  
  if (!isValid) {
    // Log non-canon fault detection
    const logData = {
      event: 'ui_non_canon_explanation',
      fault_id: normalizedId,
      ...context,
    };
    
    if (import.meta.env.DEV) {
      console.warn('[canon-validator] Non-canon fault detected:', logData);
    }
    
    // In production, emit telemetry (if telemetry system is available)
    // telemetry.emit('ui_non_canon_explanation', logData);
  }
  
  return isValid;
}

/**
 * Filter array of fault IDs to only canon faults
 * 
 * @param faultIds - Array of fault IDs to filter
 * @param context - Optional context for logging
 * @returns Array of canon fault IDs only
 */
export function filterToCanonFaults(
  faultIds: string[],
  context?: { analysis_id?: string; screen_id?: string; [key: string]: any }
): string[] {
  return faultIds.filter(faultId => {
    const isValid = validateCanonFault(faultId, context);
    return isValid;
  });
}


